import { Alert, Box, Button, IconButton } from '@mui/material';
import React, { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { localRoutes, remoteRoutes } from '../constants';
import useAuth from '../hooks/useAuth';
import { searchData } from '../utils/fetchHelpers';
import { ProductCategory } from '../__generated__/kycTypes';
import { useNavigate } from 'react-router-dom';
import { productDisplayName } from '../pages/management/settings/config';


interface IProps {
  marginTop?: number
  marginBottom?: number
  marginX?: number
  productCategories: ProductCategory[]
}

const alertDismissalDuration = 24 * 60 * 60 * 1000;

const dismissalKey = (category: string, userId: string) => `credits-alert-dismissed-${category}-${userId}`;

const saveDismissal = (category: string, userId: string) => {
  localStorage.setItem(dismissalKey(category, userId), JSON.stringify({ dismissedAt: Date.now() }));
};

const isRecentlyDismissed = (category: string, userId: string): boolean => {
  try {
    const raw = localStorage.getItem(dismissalKey(category, userId));
    if (!raw) return false;
    const { dismissedAt } = JSON.parse(raw);
    return Date.now() - dismissedAt < alertDismissalDuration;
  } catch {
    return false;
  }
};

export const CreditsUsageAlert = ({ marginTop, marginBottom, marginX, productCategories }: IProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showAlerts, setShowAlerts] = React.useState<ProductCategory[]>([]);

  useEffect(() => {
    const checkCredits = async (category: string): Promise<boolean> => {
      if (!user?.partnerId) return false;
      try {
        const response = await searchData(remoteRoutes.subscriptionCreditsUsage, {
          category,
          partnerId: user.partnerId
        });
        const isOutOfCredits = response?.creditsBalance === 0;
        return isOutOfCredits && !(user?.id && isRecentlyDismissed(category, user.id));
      } catch (error) {
        return false;
      }
    };

    const checkAllCategories = async () => {
      if (productCategories.length > 0) {
        const results = await Promise.all(productCategories.map(checkCredits));
        setShowAlerts(productCategories.filter((_, index) => results[index]));
      }
    };

    checkAllCategories();

  }, [user?.partnerId]);

  const handleAlertClose = (category: ProductCategory) => {
    if (user?.id) saveDismissal(category, user.id);
    setShowAlerts((prev) => prev.filter((visibleCategory) => visibleCategory !== category));
  };

  const handleClick = (selectedProduct: string) => {
    const targetPath = `${localRoutes.settings}?tab=plans-and-billing&product=${selectedProduct}`;
    navigate(targetPath);
  };

  return (
        <>
            {showAlerts.map((category) => (
                    <Alert
                        key={category}
                        severity="warning"
                        sx={{
                          mt: marginTop ?? 0,
                          mb: marginBottom ?? 0,
                          mx: marginX ?? 0,
                          pt: 1,
                          pb: 2
                        }}
                        action={
                            <Box>
                              <Button
                                  color="inherit"
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleClick(category)}
                              >
                                Buy More Credits
                              </Button>
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    sx={{ ml: 2 }}
                                    onClick={() => handleAlertClose(category)}
                                >
                                    <CloseIcon/>
                                </IconButton>
                            </Box>
                        }
                        onClose={() => handleAlertClose(category)}
                    >
                        {'You\'ve run out of '}<strong>{`${productDisplayName(category).toUpperCase()}`}</strong>{' credits. Buy more credits to avoid service interruptions.'}
                    </Alert>
            ))}
        </>
  );
};
